import { animate, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import { CookiesService } from '@app/core/services/cookies.service';
import { getProtocolHostnameAndPort } from '@app/shared/helpers/location';
import { Media } from '@app/shared/models/media';
import { Promotion } from '@app/shared/models/promotion';
import Plyr, { Options, SourceInfo } from 'plyr';
import { Constant } from 'src/constant';


@Component({
  selector: 'app-smash-promotion',
  templateUrl: './smash-promotion.component.html',
  styleUrls: ['./smash-promotion.component.scss'],
  animations: [
    trigger('teaserAnimation', [
      transition(":enter", [
        style({ opacity: 1 }),
        animate('100ms', style({ opacity: 1 })),
      ]),
      transition(":leave", [
        animate('500ms', style({ opacity: 0 }))
      ])
    ]),
    trigger('promotionApparition', [
      transition(":enter", [
        style({ opacity: 0 }),
        animate('500ms 500ms', style({ opacity: 1 })),
      ]),
    ]),
  ]
})
export class SmashPromotionComponent implements OnInit, OnChanges {

  @Input() promotion: Promotion;
  @Input() inputIndexSelected = 0;
  @Input() preview = false;

  constructor(private gtmService: GoogleTagManagerService, private cookiesService: CookiesService) { }

  localIndexSelected = 0;
  isTeaserDisplay = false;
  action: any;
  actions: any[] = Constant.callToActionsList;
  actualIndex = 0;
  loadingSpinnerDisplayed = false;
  timeoutNavigation = null;
  iframeReady = false;

  playerSource: SourceInfo;
  playerOptions: Options;

  ngOnInit() { }

  ngOnChanges(changes: SimpleChanges) {
    // When input promotion changes
    if (!!changes?.promotion?.currentValue) {
      clearTimeout(this.timeoutNavigation);
      this.setupActionButton();
      this.replay();
      this.localIndexSelected = 0;
      this.buildMediaIfNecessary();
    }

    // When user select a specific media
    if (changes.inputIndexSelected && this.promotion?.medias?.length > 0) {
      clearTimeout(this.timeoutNavigation);
      this.localIndexSelected = this.inputIndexSelected;
      this.buildMediaIfNecessary();
    }
  }

  buildMediaIfNecessary() {
    if (this.promotion?.medias) {
      const selectedMedia = this.promotion.medias[this.localIndexSelected];
      if (!this.preview) {
        if (selectedMedia?.type === 'Youtube' || selectedMedia?.type === 'Vimeo') {
          this.buildPlayer(selectedMedia);
          clearTimeout(this.timeoutNavigation);
        }
      }
    }
  }

  setupActionButton() {
    if (this.promotion?.button) {
      this.action = this.actions.find(action => this.promotion.button === action.value) ?? this.promotion.button;
    }
  }

  replay() {
    if (!this.promotion?.teaserDisabled) {
      this.isTeaserDisplay = true;
    }
    this.localIndexSelected = 0;
    this.buildMediaIfNecessary();
    setTimeout(() => {
      this.isTeaserDisplay = false;
    }, 5000);
  }

  navigateMediaLeft() {
    if (this.localIndexSelected > 0) {
      --this.localIndexSelected;
    } else {
      this.localIndexSelected = this.promotion.medias.length - 1;
    }
    this.buildMediaIfNecessary();
  }

  navigateMediaRight() {
    if (this.localIndexSelected < this.promotion.medias.length - 1) {
      ++this.localIndexSelected;
    } else {
      this.localIndexSelected = 0;
    }
    this.buildMediaIfNecessary();
  }

  /* When image emit event ready, keep image displayed for 5 sec */
  initTimerImage() {
    clearTimeout(this.timeoutNavigation);
    this.timeoutNavigation = setTimeout(() => {
      this.navigateMediaRight();
    }, 5000);
  }

  openPromotionLink(event: Event) {
    window.open(
      getProtocolHostnameAndPort() + '/redirect?url=' + encodeURIComponent(this.promotion.link),
      '_blank'
    );
    this.gtmService.pushTag({
      event: Constant.eventGTM.CLICK_PROMOTION_CTA,
      promotion_title: this.promotion.title,
      promotion_id: this.promotion.id,
      promotion_target: this.promotion.target,
      promotion_link: this.promotion.link,
      promotion_language: this.promotion.language,
    });
    this.gtmService.pushTag({ event: Constant.eventGTM.CLICK_REDIRECTION_CAMPAIGN, theme_link: this.promotion.link });
    event.stopPropagation();
  }

  // Player methods
  buildPlayer(media: Media) {
    this.playerSource = this.buildPlayerSource(media);
    this.playerOptions = this.buildPlayerOptions(media);
  }

  buildPlayerSource(media: Media): SourceInfo {
    const source: SourceInfo = {
      sources: [{
        src: media.sourceId,
        provider: Constant.videoProviders[media.type],
      }],
      type: 'video',
    }
    return source;
  }

  buildPlayerOptions(media: Media): Options {
    const options: Options = {
      autoplay: true,
      loop: { active: this.promotion.medias.length > 1 ? false : true },
      controls: ['play', 'progress', 'volume'],
      volume: 100,
      disableContextMenu: true,
      storage: { enabled: false }
    };

    options.controls = ['play', 'progress', 'volume', 'speed', 'duration', 'mute', 'settings', 'fullscreen'];
    
    if (media.type === 'Youtube') {
      options.youtube = { noCookie: true, rel: 0, controls: 0, showinfo: 0, iv_load_policy: 3, modestbranding: 1, origin: location.origin };
    }

    if (media.type === 'Vimeo') {
      options.vimeo = { dnt: 1, byline: false, portrait: false, title: false, speed: true, transparent: false };
    }
    
    return options;
  }
}
